import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ArrowRight, ArrowLeft, Box, Layers } from "lucide-react";
import { fetchDisplayHomes } from "../../api/public.api.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";
import { cn } from "../../lib/cn.js";

const STATUS_LABELS = {
  open: "Open",
  for_sale_lease: "For Sale / Lease",
  ex_display: "Ex-Display",
};

/* ----------------------------------------------------------------------------
   Slug helpers — `locationGroup` is free-text in the DB; we slugify for the
   URL and reverse it to match. Two homes that have minor casing/punctuation
   differences in their locationGroup string will still group correctly.
   ---------------------------------------------------------------------------- */
const toSlug = (s) =>
  String(s ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * /display-homes — landing of LOCATION cards (matches the facades pattern).
 * /display-homes/location/:locationSlug — gallery for that location only.
 *
 * The :slug detail page lives at /display-homes/:slug and is handled by
 * DisplayHomeDetailPage in routes.jsx.
 */
export default function DisplayHomesPage() {
  const { locationSlug } = useParams();
  if (locationSlug) return <LocationGalleryView locationSlug={locationSlug} />;
  return <LocationsLandingView />;
}

/* ============================================================================
   Landing — one card per locationGroup
   ============================================================================ */

function LocationsLandingView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["public", "display-homes", "all"],
    queryFn: () => fetchDisplayHomes({ limit: 50 }),
    staleTime: 30_000,
    retry: false,
  });
  const homes = data?.items ?? [];
  if (error) {
    console.error("[DisplayHomes] fetch failed", error?.response?.data ?? error);
  }

  // Group by locationGroup. Capture the first home as the card preview.
  const buckets = new Map();
  for (const h of homes) {
    const name = h.locationGroup || "Other locations";
    const key = toSlug(name);
    if (!buckets.has(key)) buckets.set(key, { key, name, homes: [] });
    buckets.get(key).homes.push(h);
  }
  const cards = [...buckets.values()];

  return (
    <>
      <SEO
        title="Display Homes"
        description="Walk through our display homes across Glen Waverley, Wollert, and Lucas — open weekends and by appointment."
      />

      <PageHero
        eyebrow="Display homes"
        title="Walk through, then build it"
        description="Pick a location to see every display home open in that area — facades, finishes, and inclusions you can experience in person."
      />

      <Container className="py-12 lg:py-16">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-3xl bg-bg-elevated" />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            Display homes coming soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
            {cards.map((c, i) => (
              <LocationCard key={c.key || i} card={c} index={i} />
            ))}
          </div>
        )}
      </Container>
    </>
  );
}

function LocationCard({ card, index }) {
  const sample = card.homes[0];
  // Prefer the display home's own heroImage / images, then fall back to
  // the linked design's heroImage. Without the heroImage fall-backs,
  // display homes that only have a hero (no gallery) used to show the
  // generic placeholder.
  const heroImage =
    sample?.heroImage ||
    sample?.images?.[0] ||
    sample?.linkedDesign?.heroImage ||
    sample?.linkedDesign?.images?.[0];
  const cardKey = card.key || `loc-${index}`;
  const heroSrc = imageOrPlaceholder(heroImage, { pool: "display", key: cardKey });
  const openCount = card.homes.filter((h) => h.status === "open").length;

  return (
    <Link
      to={`/display-homes/location/${card.key}`}
      className="group relative block aspect-[4/5] overflow-hidden rounded-3xl border border-border-subtle bg-bg-elevated"
    >
      <img
        src={heroSrc}
        onError={imgErrorFallback(cardKey)}
        alt={card.name}
        loading={index === 0 ? "eager" : "lazy"}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/40 to-transparent" />

      {/* Top-right: count of homes in this location */}
      <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-bg-base/70 px-3 py-1 text-xs text-accent backdrop-blur">
        <Layers className="h-3.5 w-3.5" />
        {card.homes.length} home{card.homes.length === 1 ? "" : "s"}
      </div>

      <div className="relative flex h-full flex-col justify-end p-6 lg:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-accent">Display location</p>
        <h2 className="mt-2 font-display text-3xl text-text-primary lg:text-4xl">{card.name}</h2>
        {openCount > 0 && (
          <p className="mt-2 text-sm text-text-secondary">
            <span className="text-success">●</span> {openCount} open right now
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1 text-sm text-accent transition-transform group-hover:translate-x-1">
          See homes in this location
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

/* ============================================================================
   Gallery — homes in a single location
   ============================================================================ */

function LocationGalleryView({ locationSlug }) {
  const { data, isLoading } = useQuery({
    queryKey: ["public", "display-homes", "all"],
    queryFn: () => fetchDisplayHomes({ limit: 50 }),
    staleTime: 30_000,
    retry: false,
  });
  const allHomes = data?.items ?? [];
  const homes = allHomes.filter(
    (h) => toSlug(h.locationGroup || "Other locations") === locationSlug
  );
  const locationName =
    homes[0]?.locationGroup ||
    // Reverse the slug into a readable label when the URL hits a location
    // that has no homes yet — better than showing the URL slug verbatim.
    locationSlug
      .split("-")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");

  return (
    <>
      <SEO
        title={`${locationName} display homes`}
        description={`Display homes open in ${locationName} — walk through every facade and finish in person.`}
      />

      <PageHero
        eyebrow="Display location"
        title={locationName}
        description="Every display home open in this area. Click through for opening hours, the linked design, and a 3D tour where available."
        breadcrumbs={[{ label: "Display Homes", to: "/display-homes" }, { label: locationName }]}
      />

      <Container className="py-12 lg:py-16">
        {/* Back link only — no chip filters per design feedback */}
        <div className="mb-10 flex items-center">
          <Link
            to="/display-homes"
            className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-secondary hover:border-accent hover:text-accent"
          >
            <ArrowLeft className="h-3 w-3" /> All locations
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-bg-elevated" />
            ))}
          </div>
        ) : homes.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            No display homes in this location yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {homes.map((h) => (
              <DisplayHomeCard key={h._id ?? h.slug} home={h} />
            ))}
          </div>
        )}
      </Container>
    </>
  );
}

function DisplayHomeCard({ home }) {
  const heroImage =
    home.heroImage ||
    home.images?.[0] ||
    home.linkedDesign?.heroImage ||
    home.linkedDesign?.images?.[0];
  const heroSrc = imageOrPlaceholder(heroImage, {
    pool: "display",
    key: home.slug ?? home._id ?? home.name,
  });
  const statusLabel = STATUS_LABELS[home.status] ?? home.status;

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-card-hover">
      <div className="relative aspect-[16/10] overflow-hidden">
        <Link to={`/display-homes/${home.slug}`} className="block h-full w-full">
          <img
            src={heroSrc}
            onError={imgErrorFallback(home.slug ?? home._id ?? home.name)}
            alt={home.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <span
          className={cn(
            "pointer-events-none absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur",
            home.status === "open"
              ? "bg-success/80 text-white"
              : home.status === "for_sale_lease"
                ? "bg-warning/80 text-bg-base"
                : "bg-bg-base/80 text-text-secondary"
          )}
        >
          {statusLabel}
        </span>

        {home.matterportUrl && (
          <a
            href={home.matterportUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`View ${home.name} 3D tour in Matterport`}
            className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-bg-base/80 px-3 py-1.5 text-xs font-medium text-accent shadow-md backdrop-blur transition-colors hover:border-accent hover:bg-accent hover:text-bg-base"
          >
            <Box className="h-3.5 w-3.5" />
            <span>3D Tour</span>
          </a>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl text-text-primary">
          <Link to={`/display-homes/${home.slug}`} className="hover:text-accent">
            {home.name}
          </Link>
        </h3>
        {home.address && (
          <p className="mt-1 inline-flex items-start gap-1 text-sm text-text-muted">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {home.address}
          </p>
        )}

        {home.linkedDesign && (
          <p className="mt-3 text-xs text-text-secondary">
            Featuring the <span className="text-accent">{home.linkedDesign.name}</span> design
          </p>
        )}

        <div className="mt-auto flex items-center justify-between gap-3 pt-5">
          {home.googleMapsUrl ? (
            <a
              href={home.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-text-muted hover:text-accent"
            >
              Get directions →
            </a>
          ) : (
            <span />
          )}
          <Link
            to={`/display-homes/${home.slug}`}
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            Details
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
